import React, { useState, useEffect, useMemo } from "react";

/* ============================================================================
   CommunityReports — context-aware crowdsource widget
   Props:
     npi         {string}  — the provider NPI (from FacilityCard result)
     cptCode     {string}  — the CPT code (internal, never shown to user)
     payer       {string|null} — selected payer or null for "all"
     negotiated  {object|null} — {lo, hi, typical} from rangeByClass (delta bar baseline)
     apiBase     {string}  — Railway API base URL (default: empty = same origin)

   Data flows to /api/community-report (POST) and /api/community-reports (GET).
   PHI gate runs client-side first; server validates again before insert.
   Aggregate-only display — never shows individual reports.
   ============================================================================ */

const COMMENT_MAX = 400;
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const YEARS  = ["2026","2025","2024"];
const money  = (n) => n == null ? "—" : "$" + Math.round(n).toLocaleString();

function scanForPHI(text) {
  if (!text || !text.trim()) return { clean: true, reasons: [] };
  const reasons = [];
  if (/\d[\d\s\-]{6,}\d/.test(text))                              reasons.push("account/ID number");
  if (/\b\d{3}-\d{2}-\d{4}\b/.test(text))                        reasons.push("SSN pattern");
  if (/[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}/i.test(text))   reasons.push("email address");
  if (/\(?\b\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}\b/.test(text))   reasons.push("phone number");
  if (/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/.test(text))               reasons.push("exact date");
  if (/\b(?:my (?:son|daughter|wife|husband|mother|father|child|kid)|member id|policy number|mrn)\b/i.test(text))
                                                                   reasons.push("personal details");
  return { clean: reasons.length === 0, reasons };
}

export default function CommunityReports({ npi, cptCode, payer, negotiated, apiBase = "" }) {
  const [view,    setView]    = useState("browse");   // browse | add | done
  const [data,    setData]    = useState(null);        // aggregate from API
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // form state
  const [comment, setComment] = useState("");
  const [total,   setTotal]   = useState("");
  const [month,   setMonth]   = useState("");
  const [year,    setYear]    = useState("2026");
  const [submitting, setSubmitting] = useState(false);
  const [doneHeld,   setDoneHeld]   = useState(false);

  const phi = useMemo(() => scanForPHI(comment), [comment]);
  const userDelta = useMemo(() => {
    const v = parseFloat(total);
    if (isNaN(v) || v <= 0 || !negotiated) return null;
    return { v, mult: (v / negotiated.typical).toFixed(1), over: v > negotiated.hi };
  }, [total, negotiated]);

  // fetch aggregate on mount and after submission
  function fetchData() {
    if (!npi || !cptCode) return;
    setLoading(true); setError(null);
    fetch(`${apiBase}/api/community-reports?npi=${npi}&cpt_code=${cptCode}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError("Could not load reports."); setLoading(false); });
  }
  useEffect(() => { fetchData(); }, [npi, cptCode]);

  async function submit() {
    if (!phi.clean || !comment.trim()) return;
    setSubmitting(true);
    try {
      const body = {
        npi, cpt_code: cptCode, payer: payer || null,
        comment: comment.trim(),
        total_charged: total ? Math.round(parseFloat(total)) : null,
        month_year: month ? `${month} ${year}` : (total ? year : null),
      };
      const r = await fetch(`${apiBase}/api/community-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok) {
        setDoneHeld(d.held);
        setView("done");
        setComment(""); setTotal(""); setMonth("");
        fetchData(); // refresh aggregate
      } else {
        setError(d.error || "Submission failed.");
      }
    } catch (e) {
      setError("Submission failed — please try again.");
    }
    setSubmitting(false);
  }

  return (
    <div style={S.wrap} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
      <div style={S.head}>
        <span style={S.title}>Community reports</span>
        <div style={S.tabs}>
          <button style={view === "browse" ? {...S.tab, ...S.tabOn} : S.tab} onClick={() => setView("browse")}>
            {data ? `${data.total} report${data.total === 1 ? "" : "s"}` : "Reports"}
          </button>
          <button style={view === "add" ? {...S.tab, ...S.tabOn} : S.tab} onClick={() => setView("add")}>
            Add yours
          </button>
        </div>
      </div>

      {/* ── BROWSE ── */}
      {view === "browse" && (
        <div>
          {loading && <p style={S.muted}>Loading…</p>}
          {error && <p style={S.muted}>{error}</p>}
          {!loading && data && (
            <>
              {negotiated && data.total_range && (
                <DeltaBar nego={negotiated} reported={data.total_range} />
              )}
              {data.meets_threshold ? (
                <>
                  <div style={S.stats}>
                    <Stat label="Reports" value={data.total} />
                    <Stat label="Reported charged"
                      value={data.total_range
                        ? `${money(data.total_range.lo)}–${money(data.total_range.hi)}`
                        : "—"} />
                  </div>
                  {data.comments.map((c, i) => (
                    <div key={i} style={S.comment}>
                      <p style={S.commentText}>{c.comment}</p>
                      <span style={S.commentMeta}>{c.when} · patient report · unverified</span>
                    </div>
                  ))}
                  {data.comments.length === 0 && (
                    <p style={S.muted}>No approved comments yet — be the first.</p>
                  )}
                </>
              ) : (
                <div style={S.below}>
                  <strong>
                    {data.total === 0
                      ? "No reports yet."
                      : `${data.total} of ${data.threshold} reports needed.`}
                  </strong>{" "}
                  We show community prices once {data.threshold} people report this procedure here —
                  so no single bill is ever exposed.
                  <button style={S.cta} onClick={() => setView("add")}>Add your report →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── ADD ── */}
      {view === "add" && (
        <div>
          <label style={S.field}>
            <span style={S.label}>Your billing experience</span>
            <textarea
              value={comment} rows={3}
              style={S.textarea}
              onChange={e => setComment(e.target.value.slice(0, COMMENT_MAX))}
              placeholder="What happened with the bill? What surprised you? No names, account numbers, or exact dates please."
            />
            <div style={S.metaRow}>
              <span style={{...S.count, ...(comment.length > COMMENT_MAX - 40 ? S.countWarn : {})}}>
                {comment.length}/{COMMENT_MAX}
              </span>
              {!phi.clean && <span style={S.phiWarn}>Remove {phi.reasons[0]} before posting.</span>}
            </div>
          </label>

          <div style={S.optHead}><span style={S.optLabel}>Optional</span>
            <span style={S.optSub}>helps compare against the negotiated rate</span></div>
          <div style={S.optRow}>
            <label style={S.field}>
              <span style={S.label}>Total charged</span>
              <div style={S.moneyWrap}>
                <span style={S.moneySym}>$</span>
                <input style={S.moneyInput} inputMode="decimal" value={total}
                  onChange={e => setTotal(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" />
              </div>
            </label>
            <label style={S.field}>
              <span style={S.label}>Roughly when</span>
              <div style={S.whenWrap}>
                <select style={S.select} value={month} onChange={e => setMonth(e.target.value)}>
                  <option value="">Month</option>
                  {MONTHS.map(m => <option key={m}>{m}</option>)}
                </select>
                <select style={S.selectSm} value={year} onChange={e => setYear(e.target.value)}>
                  {YEARS.map(y => <option key={y}>{y}</option>)}
                </select>
              </div>
            </label>
          </div>

          {userDelta && (
            <p style={{...S.verdict, ...(userDelta.over ? S.verdictBad : S.verdictOk)}}>
              {userDelta.over
                ? `You were charged ${userDelta.mult}× the negotiated rate.`
                : `About ${userDelta.mult}× the negotiated rate — within range.`}
            </p>
          )}

          {error && <p style={S.errMsg}>{error}</p>}

          <button style={{...S.submit, ...(!comment.trim() || !phi.clean ? S.submitDisabled : {})}}
            disabled={!comment.trim() || !phi.clean || submitting}
            onClick={submit}>
            {submitting ? "Posting…" : !phi.clean ? "Remove personal info to post" : "Post report"}
          </button>
          <p style={S.terms}>
            Reports are public and shown only in aggregate. Don't post names, account numbers, or exact dates.
            We review every comment before it appears. Not medical or financial advice.
          </p>
        </div>
      )}

      {/* ── DONE ── */}
      {view === "done" && (
        <div style={S.done}>
          <div style={S.check}>✓</div>
          <p style={S.doneMsg}>
            {doneHeld
              ? "Comment sent for review — it'll appear once approved. Your total is already counted."
              : "Posted — your total is counted toward the community range."}
          </p>
          <button style={S.ghost} onClick={() => setView("browse")}>See all reports</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={S.stat}>
      <span style={S.statV}>{value}</span>
      <span style={S.statL}>{label}</span>
    </div>
  );
}

function DeltaBar({ nego, reported }) {
  const max = Math.max(nego.hi * 1.15, reported?.hi || 0) || nego.hi;
  const pct = v => `${Math.min(100, (v / max) * 100)}%`;
  const typPct = nego.typical ? pct(nego.typical) : "50%";
  return (
    <div style={S.bar}>
      <div style={S.barTrack}>
        <div style={{...S.barBand, left: pct(nego.lo), width: `calc(${pct(nego.hi)} - ${pct(nego.lo)})`}} />
        <div style={{...S.barTyp, left: typPct}} />
        {reported && <>
          <div style={{...S.barMark, left: pct(reported.lo)}}><span style={S.barLabel}>reported {money(reported.lo)}</span></div>
          <div style={{...S.barMark, left: pct(reported.hi)}}><span style={S.barLabel}>{money(reported.hi)}</span></div>
        </>}
      </div>
      <div style={S.barLegend}>
        <span style={{...S.legendDot, background:"#cfe3d8"}} /> negotiated range
        <span style={{...S.legendDot, background:"#1f7a52", marginLeft:8}} /> typical
        <span style={{...S.legendDot, background:"#8a6d3b", marginLeft:8}} /> reported
      </div>
    </div>
  );
}

const S = {
  wrap:     { marginTop: 20, paddingTop: 16, borderTop: "1px solid #f3f4f6" },
  head:     { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 },
  title:    { fontSize:13, fontWeight:700, color:"#374151" },
  tabs:     { display:"flex", gap:4 },
  tab:      { font:"inherit", fontSize:12, fontWeight:600, color:"#9ca3af", background:"none", border:"1px solid transparent", padding:"4px 10px", borderRadius:999, cursor:"pointer" },
  tabOn:    { color:"#111827", border:"1px solid #e5e7eb", background:"#fff" },
  muted:    { fontSize:13, color:"#9ca3af", margin:"8px 0" },
  stats:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 },
  stat:     { background:"#f9fafb", border:"1px solid #f3f4f6", borderRadius:10, padding:"10px 12px" },
  statV:    { display:"block", fontSize:15, fontWeight:800 },
  statL:    { display:"block", fontSize:11, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.04em", marginTop:2 },
  comment:  { borderTop:"1px solid #f3f4f6", padding:"10px 0" },
  commentText: { margin:"0 0 3px", fontSize:13.5, lineHeight:1.5, color:"#111827" },
  commentMeta: { fontSize:11, color:"#9ca3af" },
  below:    { background:"#f9fafb", border:"1px dashed #e5e7eb", borderRadius:10, padding:14, fontSize:13, color:"#6b7280", lineHeight:1.55 },
  cta:      { display:"block", marginTop:10, font:"inherit", fontSize:13, fontWeight:600, color:"#2563eb", background:"none", border:"none", cursor:"pointer", padding:0 },
  field:    { display:"block", marginBottom:12 },
  label:    { display:"block", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em", color:"#9ca3af", marginBottom:5 },
  textarea: { width:"100%", font:"inherit", fontSize:14, color:"#111827", border:"1px solid #e5e7eb", borderRadius:8, padding:"9px 11px", resize:"vertical", lineHeight:1.5 },
  metaRow:  { display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4, minHeight:16 },
  count:    { fontSize:11, color:"#9ca3af" },
  countWarn:{ color:"#b5791f", fontWeight:600 },
  phiWarn:  { fontSize:11.5, color:"#b23b2e", fontWeight:600 },
  optHead:  { display:"flex", alignItems:"baseline", gap:8, marginBottom:10, paddingTop:12, borderTop:"1px solid #f3f4f6" },
  optLabel: { fontSize:13, fontWeight:700, color:"#374151" },
  optSub:   { fontSize:11.5, color:"#9ca3af" },
  optRow:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" },
  moneyWrap:{ display:"flex", alignItems:"center", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", paddingLeft:10 },
  moneySym: { fontSize:14, color:"#9ca3af" },
  moneyInput:{ border:"none", font:"inherit", fontSize:14, color:"#111827", padding:"8px 8px 8px 4px", width:"100%" },
  whenWrap: { display:"grid", gridTemplateColumns:"1fr 80px", gap:6 },
  select:   { width:"100%", font:"inherit", fontSize:13, color:"#111827", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 10px" },
  selectSm: { width:"100%", font:"inherit", fontSize:13, color:"#111827", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 6px" },
  verdict:  { fontSize:13, fontWeight:600, margin:"10px 0 0" },
  verdictOk:{ color:"#1f7a52" },
  verdictBad:{ color:"#b23b2e" },
  errMsg:   { fontSize:12.5, color:"#b23b2e", margin:"8px 0 0" },
  submit:   { width:"100%", font:"inherit", fontSize:14.5, fontWeight:700, color:"#fff", background:"#111827", border:"none", borderRadius:9, padding:"12px", cursor:"pointer", marginTop:8 },
  submitDisabled: { background:"#d1d5db", cursor:"not-allowed" },
  terms:    { fontSize:11, color:"#9ca3af", lineHeight:1.5, margin:"10px 0 0" },
  done:     { textAlign:"center", padding:"16px 0 8px" },
  check:    { width:40, height:40, borderRadius:"50%", background:"#e8f2ec", color:"#1f7a52", fontSize:20, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px", fontWeight:800 },
  doneMsg:  { fontSize:13.5, color:"#6b7280", lineHeight:1.55, maxWidth:360, margin:"0 auto 12px" },
  ghost:    { font:"inherit", fontSize:13, fontWeight:600, color:"#374151", background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 16px", cursor:"pointer" },
  bar:      { marginBottom:12 },
  barTrack: { position:"relative", height:40, background:"#f4f2ec", borderRadius:6 },
  barBand:  { position:"absolute", top:11, height:18, background:"#cfe3d8", borderRadius:3 },
  barTyp:   { position:"absolute", top:7, height:26, width:2, background:"#1f7a52" },
  barMark:  { position:"absolute", top:0, height:40, width:2, background:"#8a6d3b" },
  barLabel: { position:"absolute", top:-1, left:4, fontSize:10, fontWeight:700, color:"#8a6d3b", whiteSpace:"nowrap", background:"#f9fafb", padding:"0 2px" },
  barLegend:{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#9ca3af", marginTop:5, flexWrap:"wrap" },
  legendDot:{ width:9, height:9, borderRadius:2, display:"inline-block" },
};
